// nuolino-admin/src/features/notifications/notifications-stream.ts
// Hook que conecta ao BFF SSE /api/v1/events/stream e expoe a lista
// de notificacoes recebidas. Backoff automatico (lib/sse.ts) + cleanup
// no unmount.

import { useEffect, useState, useCallback } from 'react';
import { connectSse, type SseMessage } from '@/lib/sse';

export type NotificationKind =
  | 'notification'
  | 'patient.updated'
  | 'consultation.scheduled'
  | 'goal.critical'
  | 'chat.message';

export const NOTIFICATION_KIND_LABEL: Record<NotificationKind, string> = {
  notification: 'Notificacao',
  'patient.updated': 'Paciente atualizado',
  'consultation.scheduled': 'Consulta agendada',
  'goal.critical': 'Meta em estado critico',
  'chat.message': 'Nova mensagem',
};

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  timestamp: string;
  payload: Record<string, unknown>;
  read: boolean;
}

export interface UseNotificationsStreamResult {
  notifications: AppNotification[];
  clear: () => void;
  markRead: (id: string) => void;
  isConnected: boolean;
}

export function useNotificationsStream(): UseNotificationsStreamResult {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const push = useCallback((msg: SseMessage, kind: NotificationKind) => {
    setNotifications((prev) => {
      // Dedupe por id.
      if (prev.some((n) => n.id === msg.id)) return prev;
      const envelope = (msg.data as { payload?: Record<string, unknown> } | null) ?? {};
      const payload =
        envelope.payload && typeof envelope.payload === 'object'
          ? envelope.payload
          : ((msg.data as Record<string, unknown> | null) ?? {});
      const n: AppNotification = {
        id: msg.id ?? crypto.randomUUID(),
        kind,
        timestamp: new Date().toISOString(),
        payload,
        read: false,
      };
      // Cap em 50 — descarta os mais antigos.
      return [n, ...prev].slice(0, 50);
    });
  }, []);

  useEffect(() => {
    const conn = connectSse(
      '/api/v1/events/stream',
      {
        notification: (msg) => push(msg, 'notification'),
        'patient.updated': (msg) => push(msg, 'patient.updated'),
        'consultation.scheduled': (msg) => push(msg, 'consultation.scheduled'),
        'goal.critical': (msg) => push(msg, 'goal.critical'),
        'chat.message': (msg) => push(msg, 'chat.message'),
      },
      {
        onState: (state) => setIsConnected(state === 'open'),
      },
    );
    return () => conn.close();
  }, [push]);

  const clear = useCallback(() => setNotifications([]), []);
  const markRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  return { notifications, clear, markRead, isConnected };
}
